import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Table, Select, Input, Card, Space, Tabs, Button } from 'antd';
import { Link, useNavigate, useLocation } from '@tanstack/react-router';
import { ColumnProps, TablePaginationConfig } from 'antd/lib/table';
import moment from 'moment';
import UserService, { IUserSorter, STUDENTS_DOWNLOAD_PAGE_SIZE } from '@services/userService';
import './SingleStudent.less';
import { downloadCSV, downloadComprehensiveJSON, generateFilename } from './helpers';
import { isUserSuperAdmin } from '@helpers';
import EditUserModal from './EditUserModal';
import { useStudentsComprehensiveDownload } from '@hooks/users';
import { useGetCurrentUserQuery } from '@teachur/http-client';

const { Search } = Input;

const StudentsPage = () => {
  const { data: user } = useGetCurrentUserQuery();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = location.search as Record<string, string>;
  const tab = searchParams.tab || 'students';
  const [users, setUsers] = useState<User[]>();
  const [searchTerm, setSearchTerm] = useState<string>();
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
  });
  const [sorter, setSorter] = useState<IUserSorter>({
    field: 'createdAt',
    order: 'DESC',
  });
  const [loading, setLoading] = useState(false);
  const [comprehensiveLoading, setComprehensiveLoading] = useState(false);
  const [abortController, setAbortController] = useState<AbortController | null>(null);
  const [total, setTotal] = useState<number>();
  const [error, setError] = useState<string>();
  const [subscriptionStatusUser, setSubscriptionStatusUser] = useState<any>(['all']);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // React Query hook for comprehensive download
  const comprehensiveDownloadMutation = useStudentsComprehensiveDownload();

  const handleSearch = (value: any) => {
    const pager = { ...pagination };
    pager.current = 1;
    setPagination(pager);
    setSearchTerm(value);
    getUsers(pager, sorter, value, subscriptionStatusUser);
  };

  const getUsers = useCallback((p: any, s: any, st?: string, subscriptionStatusUser?: any) => {
    setLoading(true);
    UserService.getAllStudents({
      searchTerm: st,
      page: p.current || 1,
      sort: s,
      subscriptionStatusUser,
    })
      .then((response) => {
        setLoading(false);
        setTotal(response.total);
        setPagination({ ...p, total: response.total });
        setUsers(response.data);
      })
      .catch((err) => {
        console.error(err);
        setError(err);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!users && !loading) {
      getUsers(pagination, sorter, searchTerm, subscriptionStatusUser);
    }
  }, [users, loading, getUsers, pagination, sorter]);

  const handleDownloadClick = async () => {
    setLoading(true);
    const filename = generateFilename();

    try {
      const allData: any[] = [];
      let currentPage = 1;
      let hasMore = true;
      let total = 0;
      const currentSorter = sorter;
      const currentSubscriptionStatusUser = subscriptionStatusUser;
      const currentSearchTerm = searchTerm;

      while (hasMore) {
        const response = await UserService.getAllStudentsDownload({
          searchTerm: currentSearchTerm,
          page: currentPage,
          sort: currentSorter,
          subscriptionStatusUser: currentSubscriptionStatusUser,
        });

        if (currentPage === 1) {
          total = response.total;
        }

        allData.push(...response.data);

        const fetchedCount = currentPage * STUDENTS_DOWNLOAD_PAGE_SIZE;
        hasMore = fetchedCount < total;
        currentPage++;
      }

      setLoading(false);
      downloadCSV(filename, allData);
    } catch (error) {
      setLoading(false);
      console.error('Error occurred while fetching data:', error);
    }
  };

  const getUsersDownload = useCallback(
    (p: any, s: any, st?: string, subscriptionStatusUser?: any) => {
      return UserService.getAllStudentsDownload({
        searchTerm: st,
        page: p.current || 1,
        sort: s,
        subscriptionStatusUser,
      });
    },
    []
  );

  const handleComprehensiveDownloadClick = async () => {
    setComprehensiveLoading(true);
    const controller = new AbortController();
    setAbortController(controller);
    const filename = `comprehensive_student_report_${moment().format('YYYYMMDD_HHmmss')}.json`;
    comprehensiveDownloadMutation.mutate(
      {
        page: pagination.current || 1,
        sort: sorter,
        searchTerm,
        subscriptionStatusUser,
      },
      {
        onSuccess: async (response) => {
          const allData: any[] = [];
          let currentPage = 1;
          let hasMore = true;
          let total = 0;
          const currentSorter = sorter;
          const currentSubscriptionStatusUser = subscriptionStatusUser;
          const currentSearchTerm = searchTerm;

          allData.push(...response.data);
          total = response.total;

          while (hasMore) {
            if (controller.signal.aborted) {
              setComprehensiveLoading(false);
              setAbortController(null);
              return;
            }

            const chunkResponse = await UserService.getAllStudentsComprehensiveDownload({
              page: currentPage + 1,
              sort: currentSorter,
              searchTerm: currentSearchTerm,
              subscriptionStatusUser: currentSubscriptionStatusUser,
            });

            allData.push(...chunkResponse.data);
            const fetchedCount = (currentPage + 1) * STUDENTS_DOWNLOAD_PAGE_SIZE;
            hasMore = fetchedCount < total;
            currentPage++;
          }

          setComprehensiveLoading(false);
          setAbortController(null);
          downloadComprehensiveJSON(filename, allData);
        },
        onError: (error) => {
          setComprehensiveLoading(false);
          setAbortController(null);
          console.error('Error occurred while fetching comprehensive data:', error);
        },
      }
    );
  };

  const handleCancelDownload = () => {
    if (abortController) {
      abortController.abort();
      setComprehensiveLoading(false);
      setAbortController(null);
    }
  };

  const handleTableChange = (pagination: TablePaginationConfig, filters: any, sorter: any) => {
    const pager = { ...pagination };
    pager.current = pagination.current;
    const newSorter: IUserSorter = {
      field: sorter.field,
      order: sorter.order === 'descend' ? 'DESC' : 'ASC',
    };
    setPagination(pager);
    setSorter(newSorter);
    getUsers(pager, newSorter, searchTerm, subscriptionStatusUser);
  };

  const handleTabChange = (key: string) => {
    navigate({
      to: '/students',
      search: (prev: Record<string, any>) => ({ ...prev, tab: key }),
    });
  };

  const handleChange = (value: any) => {
    if (
      value.indexOf('enrolled') == -1 &&
      value.indexOf('trial') == -1 &&
      value.indexOf('registered') == -1 &&
      value.indexOf('archived') == -1 &&
      value.indexOf('on_leave') == -1 &&
      value.indexOf('graduate') == -1
    ) {
      setSubscriptionStatusUser(['all']);
      getUsers(pagination, sorter, searchTerm, ['all']);
    } else {
      const filtered = value.filter((a: any) => a != 'all');
      setSubscriptionStatusUser(filtered);
      getUsers(pagination, sorter, searchTerm, filtered);
    }
    // setUsers([]);
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setEditModalVisible(true);
  };

  const handleEditSuccess = () => {
    // Refresh the users list after successful edit
    getUsers(pagination, sorter, searchTerm, subscriptionStatusUser);
    setSelectedUser(null);
  };
  const userColumns: ColumnProps<User>[] = [
    {
      key: 'name',
      dataIndex: 'name',
      title: 'Name',
      // sorter: true,
      render: (name: any, record: User) => (
        <Link to={`/students/${record.id}`}>
          {`${record.firstName} ${record.lastName}(${record.email})`}
        </Link>
      ),
    },
    {
      title: 'Student id',
      dataIndex: 'studentId',
      key: 'studentId',
      render: (studentId: any, record: User) => studentId,
    },
    {
      title: 'Subscription status',
      dataIndex: 'subscriptionStatus',
      key: 'subscriptionStatus',
      render: (subscriptionStatus: any) =>
        subscriptionStatus == 'active'
          ? 'Active'
          : subscriptionStatus == 'trialing'
            ? 'Trialing'
            : subscriptionStatus == 'canceled'
              ? 'Canceled'
              : subscriptionStatus == 'past_due'
                ? 'Past due'
                : '',
      sorter: true,
    },
    {
      // title: 'Registered / Enrolled',
      title: 'Status',
      dataIndex: 'studentEnrolledType',
      key: 'studentEnrolledType',
      render: (studentEnrolledType: any) =>
        studentEnrolledType == 'registered'
          ? 'Registered'
          : studentEnrolledType == 'enrolled'
            ? 'Enrolled'
            : studentEnrolledType == 'trial'
              ? 'Trial'
              : studentEnrolledType == 'on_leave'
                ? 'On Leave'
                : studentEnrolledType == 'archived'
                  ? 'Archived'
                  : studentEnrolledType == 'graduate'
                    ? 'Graduate'
                    : '',
      sorter: true,
    },
    {
      title: 'Subscription type',
      dataIndex: 'subscriptionType',
      key: 'subscriptionType',
      render: (subscriptionType: any) => {
        const labels: Record<string, string> = {
          free: 'Free',
          monthly: 'Monthly',
          master: 'Master',
          lifetime: 'Lifetime',
          split: 'Split payment',
        };
        return labels[subscriptionType] ?? '';
      },
      sorter: true,
    },
    {
      title: 'Enrolled degrees',
      dataIndex: 'enrolledDegrees',
      key: 'enrolledDegrees',
      render: (enrolledDegrees: any, record: User) => (
        <div className="enrolledDegrees">
          {enrolledDegrees.enrolledDegrees.map((d: any, i: any) => (
            <span>
              {i + 1}: {d.degree.name} <br />
            </span>
          ))}
        </div>
      ),
      // sorter: (a: any, b: any) => a.enrolledDegreesCount - b.enrolledDegreesCount,
    },
    {
      title: 'PT/FT Status',
      dataIndex: 'studyType',
      key: 'studyType',
      render: (studyType: any) =>
        studyType == 'part_time' ? 'Part Time' : '' || studyType == 'full_time' ? 'Full Time' : '',
      sorter: true,
    },
    {
      title: 'Enrolled courses',
      dataIndex: 'enrolledCoursesCount',
      key: 'enrolledCoursesCount',
      sorter: true,
    },
    {
      title: 'Passed courses',
      dataIndex: 'passedCoursesCount',
      key: 'passedCoursesCount',
      sorter: true,
    },
    {
      title: 'Completed objectives',
      dataIndex: 'completedObjectivesCount',
      key: 'completedObjectivesCount',
      defaultSortOrder: 'descend',
      sorter: true,
    },
    {
      title: 'Last active',
      dataIndex: 'lastActive',
      key: 'lastActive',
      render: (lastActive?: Date) => (lastActive ? moment(lastActive).format('LLL') : '-'),
      sorter: true,
    },
    {
      title: 'Signup date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (createdAt?: Date) => (createdAt ? moment(createdAt).format('LLL') : '-'),
      sorter: true,
    },
    {
      title: 'Academic progress',
      dataIndex: 'status',
      key: 'status',
      sorter: true,
    },
  ];

  if (user && isUserSuperAdmin(user)) {
    userColumns.push({
      title: 'Actions',
      key: 'actions',
      render: (text: any, record: User) => (
        <Button type="primary" size="small" onClick={() => handleEditUser(record)}>
          Edit
        </Button>
      ),
    });
  }

  return (
    <Row gutter={16}>
      <Col span={24}>
        <Tabs
          type="card"
          activeKey={tab}
          onChange={handleTabChange}
          items={[
            {
              key: 'students',
              label: 'Students',
              children: (
                <Card title={`Students (Total: ${total})`}>
                  <Space direction="horizontal" style={{ marginBottom: 16 }}>
                    <Search
                      placeholder="Search users by email, first name and last name"
                      onSearch={handleSearch}
                      loading={loading}
                      style={{ width: 400 }}
                    />
                    <Select
                      mode="multiple"
                      className="select-filter"
                      placeholder=""
                      value={subscriptionStatusUser}
                      onChange={handleChange}
                    >
                      <Select.Option value="all">All</Select.Option>
                      <Select.Option value="registered">Registered</Select.Option>
                      <Select.Option value="enrolled">Enrolled</Select.Option>
                      <Select.Option value="trial">Trial</Select.Option>
                      <Select.Option value="on_leave">On leave</Select.Option>
                      <Select.Option value="archived">Archived</Select.Option>
                      <Select.Option value="graduate">Graduate</Select.Option>
                    </Select>
                    <Button
                      type="primary"
                      onClick={handleDownloadClick}
                      disabled={loading || comprehensiveLoading}
                      loading={loading}
                    >
                      {loading ? 'Downloading...' : 'Download All'}
                    </Button>
                    <Button
                      type="default"
                      onClick={handleComprehensiveDownloadClick}
                      disabled={loading || comprehensiveLoading}
                      loading={comprehensiveLoading}
                      style={{ background: '#52c41a', borderColor: '#52c41a', color: 'white' }}
                    >
                      📊 {comprehensiveLoading ? 'Generating...' : 'Comprehensive Report'}
                    </Button>
                    {comprehensiveLoading && (
                      <Button type="default" onClick={handleCancelDownload} danger>
                        Cancel
                      </Button>
                    )}
                  </Space>
                  <div style={{ overflowX: 'auto' }}>
                    <Table
                      className="ant-table-responsive"
                      dataSource={users}
                      columns={userColumns}
                      rowKey="id"
                      onChange={handleTableChange}
                      pagination={pagination}
                    />
                  </div>
                </Card>
              ),
            },
          ]}
        />
      </Col>

      <EditUserModal
        visible={editModalVisible}
        onClose={() => setEditModalVisible(false)}
        user={selectedUser}
        onSuccess={handleEditSuccess}
      />
    </Row>
  );
};

export default StudentsPage;
